// src/utils/fourthwall.js

import { API } from "./constants";

const STOREFRONT_TOKEN =
  process.env.REACT_APP_FW_STOREFRONT_TOKEN;

const CHECKOUT_DOMAIN =
  process.env.REACT_APP_FW_CHECKOUT_DOMAIN;

const FW_API_BASE =
  "https://storefront-api.fourthwall.com/v1";

/**
 * Public Fourthwall storefront GET request.
 * Used only for public collection/product data.
 */
async function fourthwallGet(path) {
  if (!STOREFRONT_TOKEN) {
    throw new Error(
      "Missing REACT_APP_FW_STOREFRONT_TOKEN in frontend environment."
    );
  }

  const separator =
    path.includes("?") ? "&" : "?";

  const url =
    `${FW_API_BASE}${path}` +
    `${separator}storefront_token=${encodeURIComponent(
      STOREFRONT_TOKEN
    )}`;

  const res = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
    },
  });

  if (!res.ok) {
    const text =
      await res.text();

    throw new Error(
      `Fourthwall API ${res.status}: ${text}`
    );
  }

  return res.json();
}

/**
 * Calls our own backend merch API.
 *
 * The browser never performs Fourthwall cart writes directly.
 */
async function cartProxyFetch(
  path,
  options = {}
) {
  const res = await fetch(
    `${API}/merch${path}`,
    {
      ...options,

      headers: {
        Accept:
          "application/json",

        "Content-Type":
          "application/json",

        ...(options.headers || {}),
      },

      credentials: "include",
    }
  );

  if (!res.ok) {
    const text =
      await res.text();

    throw new Error(
      `Cart API ${res.status}: ${text}`
    );
  }

  return res.json();
}

/**
 * Get products from a Fourthwall collection.
 */
export async function getCollectionProducts(
  collectionHandle
) {
  if (!collectionHandle) {
    throw new Error(
      "Missing Fourthwall collection handle."
    );
  }

  const data =
    await fourthwallGet(
      `/collections/${encodeURIComponent(
        collectionHandle
      )}/products`
    );

  if (Array.isArray(data)) {
    return data;
  }

  if (
    Array.isArray(
      data?.results
    )
  ) {
    return data.results;
  }

  if (
    Array.isArray(
      data?.products
    )
  ) {
    return data.products;
  }

  return [];
}

/**
 * Retrieve a single Fourthwall product.
 */
export async function getProduct(
  productId
) {
  if (!productId) {
    throw new Error(
      "Missing Fourthwall product ID."
    );
  }

  return fourthwallGet(
    `/products/${encodeURIComponent(
      productId
    )}`
  );
}

/**
 * Create a brand-new cart.
 *
 * We deliberately do NOT accept user metadata here.
 *
 * A visitor may build a cart while logged out.
 * The cart is securely linked to their TOA account
 * later using claimCart() immediately before checkout.
 */
export async function createCart(
  items
) {
  if (
    !Array.isArray(items) ||
    items.length === 0
  ) {
    throw new Error(
      "createCart requires at least one item."
    );
  }

  return cartProxyFetch(
    "/carts",
    {
      method: "POST",

      body: JSON.stringify({
        items,
      }),
    }
  );
}

/**
 * Add new items / variants to an existing cart.
 *
 * Use for:
 * - another product
 * - another colour
 * - another size
 * - another variant
 */
export async function addCartItems(
  cartId,
  items
) {
  if (!cartId) {
    throw new Error(
      "Missing cart ID."
    );
  }

  if (
    !Array.isArray(items) ||
    items.length === 0
  ) {
    throw new Error(
      "addCartItems requires at least one item."
    );
  }

  return cartProxyFetch(
    `/carts/${encodeURIComponent(
      cartId
    )}/add`,
    {
      method: "POST",

      body: JSON.stringify({
        items,
      }),
    }
  );
}

/**
 * Retrieve an existing cart.
 */
export async function getCart(
  cartId
) {
  if (!cartId) {
    throw new Error(
      "Missing cart ID."
    );
  }

  return cartProxyFetch(
    `/carts/${encodeURIComponent(
      cartId
    )}`,
    {
      method: "GET",
    }
  );
}

/**
 * Change quantity of an item already in the cart.
 */
export async function setCartItemQuantity(
  cartId,
  variantId,
  quantity
) {
  if (!cartId) {
    throw new Error(
      "Missing cart ID."
    );
  }

  if (!variantId) {
    throw new Error(
      "Missing variant ID."
    );
  }

  const resolvedQuantity =
    Number(quantity);

  if (
    !Number.isInteger(
      resolvedQuantity
    ) ||
    resolvedQuantity < 1 ||
    resolvedQuantity > 100
  ) {
    throw new Error(
      "Quantity must be between 1 and 100."
    );
  }

  return cartProxyFetch(
    `/carts/${encodeURIComponent(
      cartId
    )}/change`,
    {
      method: "POST",

      body: JSON.stringify({
        variantId,
        quantity:
          resolvedQuantity,
      }),
    }
  );
}

/**
 * Remove a variant completely from the cart.
 *
 * Fourthwall expects an items array.
 */
export async function removeCartItem(
  cartId,
  variantId
) {
  if (!cartId) {
    throw new Error(
      "Missing cart ID."
    );
  }

  if (!variantId) {
    throw new Error(
      "Missing variant ID."
    );
  }

  return cartProxyFetch(
    `/carts/${encodeURIComponent(
      cartId
    )}/remove`,
    {
      method: "POST",

      body: JSON.stringify({
        items: [
          {
            variantId,
          },
        ],
      }),
    }
  );
}

/**
 * Securely claim a cart for the currently authenticated TOA user.
 *
 * IMPORTANT:
 *
 * The browser sends:
 *
 *   cartId
 *   accessToken
 *
 * It does NOT send:
 *
 *   userId
 *
 * The backend verifies the JWT with requireAuth
 * and obtains the real user ID from:
 *
 *   req.user.id
 *
 * The backend then attaches that ID to the
 * Fourthwall cart as metadata.
 */
export async function claimCart(
  cartId,
  accessToken
) {
  if (!cartId) {
    throw new Error(
      "Missing cart ID."
    );
  }

  if (!accessToken) {
    throw new Error(
      "You must be logged in before checkout."
    );
  }

  return cartProxyFetch(
    `/carts/${encodeURIComponent(
      cartId
    )}/claim`,
    {
      method: "POST",

      headers: {
        Authorization:
          `Bearer ${accessToken}`,
      },

      body:
        JSON.stringify({}),
    }
  );
}

/**
 * Return total number of units in the cart.
 *
 * Example:
 *
 * Hoodie x2
 * Tee x1
 *
 * returns 3.
 */
export function cartItemCount(
  cart
) {
  if (
    !cart?.items ||
    !Array.isArray(
      cart.items
    )
  ) {
    return 0;
  }

  return cart.items.reduce(
    (
      total,
      item
    ) =>
      total +
      Number(
        item?.quantity || 0
      ),
    0
  );
}

/**
 * Generate the Fourthwall hosted checkout URL.
 *
 * REACT_APP_FW_CHECKOUT_DOMAIN should be:
 *
 * ydlive.store
 *
 * NOT:
 *
 * https://ydlive.store
 *
 * and NOT:
 *
 * ydlive.store/checkout
 */
export function getCheckoutUrl(
  cartId,
  currency = "GBP"
) {
  if (!cartId) {
    throw new Error(
      "Missing cart ID."
    );
  }

  if (!CHECKOUT_DOMAIN) {
    throw new Error(
      "Missing REACT_APP_FW_CHECKOUT_DOMAIN in frontend environment."
    );
  }

  const cleanDomain =
    CHECKOUT_DOMAIN
      .replace(
        /^https?:\/\//i,
        ""
      )
      .replace(
        /\/+$/,
        ""
      );

  return (
    `https://${cleanDomain}/checkout/` +
    `?cartCurrency=${encodeURIComponent(
      currency || "GBP"
    )}` +
    `&cartId=${encodeURIComponent(
      cartId
    )}`
  );
}